// Gamification utilities: Korean-themed titles, levels, and preset avatars

export interface TitleInfo {
  korean: string;
  english: string;
  icon: string;
}

export interface AvatarPreset {
  id: string;
  emoji: string;
  label: string;
}

// Korean-themed titles mapped to levels
const LEVEL_TITLES: Record<number, TitleInfo> = {
  1: { korean: '새내기', english: 'Newcomer', icon: '🌱' },
  2: { korean: '이웃', english: 'Neighbor', icon: '🏠' },
  3: { korean: '단골', english: 'Regular', icon: '⭐' },
  4: { korean: '도우미', english: 'Helper', icon: '🤝' },
  5: { korean: '기여자', english: 'Contributor', icon: '💪' },
  6: { korean: '안내자', english: 'Guide', icon: '🧭' },
  7: { korean: '선배', english: 'Senior', icon: '🎓' },
  8: { korean: '대사', english: 'Ambassador', icon: '🏆' },
  9: { korean: '원로', english: 'Elder', icon: '👑' },
  10: { korean: '전설', english: 'Legend', icon: '🔥' },
};

// Point thresholds for each level
const LEVEL_THRESHOLDS = [0, 100, 300, 600, 1000, 2000, 5000, 10000, 25000, 50000];

// Preset avatars with Korean-themed options
export const PRESET_AVATARS: AvatarPreset[] = [
  { id: 'default', emoji: '👤', label: 'Default' },
  { id: 'korea', emoji: '🇰🇷', label: 'Korea' },
  { id: 'hanbok', emoji: '👘', label: 'Hanbok' },
  { id: 'food', emoji: '🍜', label: 'Food Lover' },
  { id: 'traveler', emoji: '✈️', label: 'Traveler' },
  { id: 'student', emoji: '📚', label: 'Student' },
  { id: 'business', emoji: '💼', label: 'Business' },
  { id: 'music', emoji: '🎵', label: 'K-Pop Fan' },
  { id: 'chef', emoji: '👨‍🍳', label: 'Chef' },
  { id: 'artist', emoji: '🎨', label: 'Artist' },
  { id: 'tech', emoji: '💻', label: 'Tech' },
  { id: 'nature', emoji: '🌸', label: 'Nature' },
];

/**
 * Get the title information for a given level
 */
export function getTitleForLevel(level: number): TitleInfo {
  const clampedLevel = Math.max(1, Math.min(10, level));
  return LEVEL_TITLES[clampedLevel];
}

/**
 * Get the points required for a specific level
 */
export function getPointsForLevel(level: number): number {
  const index = Math.max(0, Math.min(9, level - 1));
  return LEVEL_THRESHOLDS[index];
}

/**
 * Get the points required for the next level
 */
export function getPointsForNextLevel(currentLevel: number): number {
  if (currentLevel >= 10) return LEVEL_THRESHOLDS[9]; // Max level
  return LEVEL_THRESHOLDS[currentLevel]; // Next level threshold
}

/**
 * Calculate progress percentage toward the next level
 */
export function getProgressToNextLevel(points: number, currentLevel: number): number {
  if (currentLevel >= 10) return 100; // Max level reached

  const currentThreshold = getPointsForLevel(currentLevel);
  const nextThreshold = getPointsForNextLevel(currentLevel);
  const pointsInLevel = points - currentThreshold;
  const pointsNeeded = nextThreshold - currentThreshold;

  if (pointsNeeded <= 0) return 100;
  return Math.min(100, Math.round((pointsInLevel / pointsNeeded) * 100));
}

/**
 * Get avatar preset by ID
 */
export function getAvatarPreset(id: string | null | undefined): AvatarPreset {
  if (!id) return PRESET_AVATARS[0];
  return PRESET_AVATARS.find(a => a.id === id) || PRESET_AVATARS[0];
}

/**
 * Format points with commas for display
 */
export function formatPoints(points: number): string {
  return points.toLocaleString();
}
